import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabase';
import Button from '../common/Button';
import StatusBadge from '../common/StatusBadge';
import ConfirmationModal from '../common/ConfirmationModal';
import Modal from '../common/Modal';
import ModalContent from '../common/ModalContent';
import { Heading, Text } from '../common/Typography';
import { Card } from '../common/Card';
import MobileSearchField from '../common/MobileSearchField';
import CollapsibleSection from '../common/CollapsibleSection';
import { ClubSectionIcon, MANAGEMENT_ICON_TONES } from '../clubs/dashboard/ClubSectionIcons';
import { deleteSession, sessionHasPaymentRecords } from '../../services/sessionManagementService';
import {
  fetchSessionActivityCountMap,
  mergeSessionActivityCounts,
} from '../../services/sessionActivityStatsService';
import { getSessionStatusLabel, isHubPastSessionListItem, sessionRequiresPaymentWrapUp } from './hub/sessionHubUtils';
import SessionPastMobileCard from './hub/SessionPastMobileCard';
import { format } from 'date-fns';

const SessionMonitoring = ({
  clubId,
  embedded = false,
  variant = 'all',
  excludeSessionIds = null,
  completedSessionAllMarkedFromHub = null,
  onSessionsChange,
  clubLogoUrl = null,
  clubName = '',
}) => {
  const navigate = useNavigate();
  
  // Session monitoring state
  const [sessions, setSessions] = useState({
    ongoing: [],
    completed: [],
    archived: []
  });
  const [sessionsByTime, setSessionsByTime] = useState({
    today: [],
    thisWeek: [],
    older: [],
    archived: []
  });
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionActivityStatus, setSessionActivityStatus] = useState(null);
  
  // Pagination state for archived sessions
  const [archivedPagination, setArchivedPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalItems: 0
  });
  
  // Filtering state
  const [searchTerm, setSearchTerm] = useState('');

  // Delete session (for 0-game completed competitive sessions)
  const [sessionToDelete, setSessionToDelete] = useState(null);
  // For completed sessions: sessionId -> true if all players are marked paid or no_show
  const [completedSessionAllPlayersMarked, setCompletedSessionAllPlayersMarked] = useState({});
  // Track which sessions have payment records (sessionId -> boolean)
  const [sessionsWithPayments, setSessionsWithPayments] = useState({});
  // Which session's Actions dropdown is open (session object or null)
  const [openActionsSession, setOpenActionsSession] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const actionsDropdownRef = useRef(null);
  const actionsPortalRef = useRef(null);
  const DROPDOWN_WIDTH = 240;

  const measureActionsDropdownPosition = useRef(() => {});
  measureActionsDropdownPosition.current = () => {
    const el = actionsDropdownRef.current;
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    // Skip until layout is real — zero-sized rects pin the portal to the top-left (mobile / hidden rows).
    if (rect.width < 4 || rect.height < 4 || rect.bottom <= rect.top) return false;
    const maxLeft = Math.max(8, window.innerWidth - DROPDOWN_WIDTH - 8);
    const left = Math.max(8, Math.min(rect.right - DROPDOWN_WIDTH, maxLeft));
    setDropdownPosition({
      top: rect.bottom + 4,
      left,
    });
    return true;
  };

  // After open, refs attach on the active row/card — measure in layout phase and retry so the portal can show on mobile.
  useLayoutEffect(() => {
    if (!openActionsSession) {
      setDropdownPosition(null);
      return;
    }
    setDropdownPosition(null);
    let cancelled = false;
    let attempt = 0;
    const tryMeasure = () => {
      if (cancelled) return;
      if (measureActionsDropdownPosition.current()) return;
      attempt += 1;
      if (attempt < 20) {
        requestAnimationFrame(tryMeasure);
      }
    };
    tryMeasure();
    return () => {
      cancelled = true;
    };
  }, [openActionsSession]);

  // Keep dropdown anchored when user scrolls or resizes
  useEffect(() => {
    if (!openActionsSession) return;
    const measure = () => {
      measureActionsDropdownPosition.current();
    };
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [openActionsSession]);

  // Close when tapping outside. Defer attaching so the same touch/click that opens the menu does not close it (mobile).
  useEffect(() => {
    if (!openActionsSession) return;
    let remove = () => {};
    const timer = window.setTimeout(() => {
      const handlePointerDown = (e) => {
        const node = e.target;
        if (!(node instanceof Node)) return;
        if (actionsDropdownRef.current?.contains(node)) return;
        if (actionsPortalRef.current?.contains(node)) return;
        setOpenActionsSession(null);
      };
      document.addEventListener('pointerdown', handlePointerDown, true);
      remove = () => document.removeEventListener('pointerdown', handlePointerDown, true);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      remove();
    };
  }, [openActionsSession]);

  // Fetch sessions when club changes
  useEffect(() => {
    if (clubId) {
      fetchSessions();
    }
  }, [clubId]);

  useEffect(() => {
    if (embedded && completedSessionAllMarkedFromHub) {
      setCompletedSessionAllPlayersMarked(completedSessionAllMarkedFromHub);
    }
  }, [embedded, completedSessionAllMarkedFromHub]);

  const refreshSessions = async ({ syncHub = false } = {}) => {
    await fetchSessions();
    if (syncHub) {
      onSessionsChange?.();
    }
  };

  // Update pagination total when archived sessions change
  useEffect(() => {
    const filteredArchived = filterSessions(sessionsByTime.archived);
    setArchivedPagination(prev => ({
      ...prev,
      totalItems: filteredArchived.length,
      currentPage: 1 // Reset to first page when search changes
    }));
  }, [searchTerm, sessionsByTime.archived]);




  const fetchSessions = async () => {
    if (!clubId) return;
    
    setSessionsLoading(true);
    try {
      let sessionsQuery = supabase
        .from('sessions')
        .select(`
          *,
          session_types:session_type_id (
            id,
            name
          )
        `)
        .eq('club_id', clubId)
        .order('session_date', { ascending: true });

      if (variant === 'past') {
        sessionsQuery = sessionsQuery.in('session_status', ['ongoing', 'completed', 'cancelled']);
      } else if (variant === 'archived') {
        sessionsQuery = sessionsQuery.eq('session_status', 'archived');
      } else {
        sessionsQuery = sessionsQuery.neq('session_status', 'draft');
      }

      const { data, error } = await sessionsQuery;

      if (error) throw error;

      const sessionList = data || [];
      const activitySessionIds = sessionList
        .filter((s) => s.session_status === 'ongoing' || s.session_status === 'completed')
        .map((s) => s.id);
      const activityCountMap = await fetchSessionActivityCountMap(activitySessionIds);
      const sessionsWithStats = mergeSessionActivityCounts(sessionList, activityCountMap);

      // For completed sessions: check if all players are marked paid or no_show (for Tasks column + Archive)
      const completedIds = sessionsWithStats
        .filter((s) => s.session_status === 'completed')
        .map((s) => s.id);

      if (completedSessionAllMarkedFromHub && completedIds.length > 0) {
        setCompletedSessionAllPlayersMarked(completedSessionAllMarkedFromHub);
      } else if (completedIds.length > 0) {
        // Get all session_players for completed sessions
        const { data: playersRows } = await supabase
          .from('session_players')
          .select('id, session_id, exit_reason')
          .in('session_id', completedIds);
        
        // Get payment allocations to determine paid status
        const sessionPlayerIds = (playersRows || []).map(sp => sp.id);
        const { data: paidAllocations } = await supabase
          .from('payment_allocations')
          .select(`
            session_player_id,
            payment_ledger:payment_id!inner(is_deleted)
          `)
          .in('session_player_id', sessionPlayerIds);
        
        // Build set of paid session_player_ids
        const paidSessionPlayerIds = new Set();
        (paidAllocations || []).forEach(alloc => {
          if (alloc.payment_ledger && !alloc.payment_ledger.is_deleted) {
            paidSessionPlayerIds.add(alloc.session_player_id);
          }
        });
        
        const bySession = {};
        completedIds.forEach((id) => { bySession[id] = []; });
        (playersRows || []).forEach((row) => { 
          bySession[row.session_id].push({
            ...row,
            session_payment: paidSessionPlayerIds.has(row.id) ? 'paid' : 'not_paid'
          });
        });
        const allMarked = {};
        completedIds.forEach((sid) => {
          const players = bySession[sid] || [];
          allMarked[sid] = players.length === 0 || players.every((p) => p.session_payment === 'paid' || p.exit_reason === 'no_show');
        });
        setCompletedSessionAllPlayersMarked(allMarked);
      } else if (!completedSessionAllMarkedFromHub) {
        setCompletedSessionAllPlayersMarked({});
      }

      // Check which sessions have payment records (to prevent deletion)
      const allSessionIds = sessionsWithStats.map((s) => s.id);
      if (allSessionIds.length > 0) {
        const paymentsCheck = {};
        // Check payment_allocations table for any payments
        const { data: paidSessionPlayers } = await supabase
          .from('payment_allocations')
          .select(`
            session_player:session_player_id!inner(session_id),
            payment_ledger:payment_id!inner(is_deleted)
          `)
          .in('session_player.session_id', allSessionIds);
        
        (paidSessionPlayers || []).forEach(row => {
          // Only count active payments (not deleted)
          if (row.payment_ledger && !row.payment_ledger.is_deleted && row.session_player) {
            paymentsCheck[row.session_player.session_id] = true;
          }
        });
        setSessionsWithPayments(paymentsCheck);
      } else {
        setSessionsWithPayments({});
      }

      // Categorize sessions by status
      const categorized = {
        ongoing: sessionsWithStats.filter((s) => s.session_status === 'ongoing'),
        completed: sessionsWithStats.filter((s) => s.session_status === 'completed'),
        archived: sessionsWithStats.filter((s) => s.session_status === 'archived'),
      };

      // Categorize sessions by time
      const timeCategorized = categorizeSessionsByTime(sessionsWithStats);

      setSessions(categorized);
      setSessionsByTime(timeCategorized);
    } catch (err) {
      console.error('Error fetching sessions:', err);
    } finally {
      setSessionsLoading(false);
    }
  };



  const updateSessionStatus = async (sessionId, newStatus) => {
    try {
      const { error } = await supabase
        .from('sessions')
        .update({ session_status: newStatus })
        .eq('id', sessionId);

      if (error) throw error;

      // Refresh sessions to show updated status
      await refreshSessions({ syncHub: true });
    } catch (err) {
      console.error('Error updating session status:', err);
    }
  };

  const isCompetitiveSession = (session) =>
    session?.session_types?.name === 'Competitive Session';

  const canDeleteSession = (session) =>
    isCompetitiveSession(session) &&
    session.session_status === 'completed' &&
    (session.games_count ?? 0) === 0;

  const handleConfirmDeleteSession = async () => {
    if (!sessionToDelete) return;

    try {
      await deleteSession(sessionToDelete.id);
      setSessionToDelete(null);
      try {
        await refreshSessions({ syncHub: true });
      } catch (refreshErr) {
        console.error('Error refreshing sessions after delete:', refreshErr);
      }
    } catch (err) {
      console.error('Error deleting session:', err);
      alert(err.message || 'Failed to delete session. Please try again.');
    }
  };

  const getSessionActivityStatus = async (sessionId) => {
    try {
      // Query the session data directly instead of using the RPC function
      // to avoid enum type issues
      const { data, error } = await supabase
        .from('sessions')
        .select(`
          id,
          name,
          session_status,
          session_date,
          start_time,
          clubs:club_id (
            name
          )
        `)
        .eq('id', sessionId)
        .single();

      if (error) throw error;
      
      // Get games count for this session
      const { count: gamesCount } = await supabase
        .from('session_games')
        .select('*', { count: 'exact', head: true })
        .eq('session_id', sessionId);

      // Get last game timestamp
      const { data: lastGame } = await supabase
        .from('session_games')
        .select('created_at')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      // Calculate hours since last activity
      const lastActivity = lastGame?.created_at || data.session_date;
      const hoursSinceActivity = lastActivity ? 
        (new Date() - new Date(lastActivity)) / (1000 * 60 * 60) : 0;

      setSessionActivityStatus({
        session_id: data.id,
        session_name: data.name,
        club_name: data.clubs?.name || 'Unknown Club',
        session_status: data.session_status,
        session_started_at: data.session_date,
        last_game_at: lastGame?.created_at || null,
        games_count: gamesCount || 0,
        hours_since_last_activity: hoursSinceActivity,
        auto_end_threshold_hours: 4, // Default value
        will_auto_end_soon: false // Default value
      });
    } catch (err) {
      console.error('Error getting session activity status:', err);
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    // timeString is now a TIME type (HH:mm:ss), so we need to create a date object
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    return format(date, 'h:mm a');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return format(new Date(dateString), 'MMM dd, yyyy');
  };

  const categorizeSessionsByTime = (allSessions) => {
    const today = new Date();
    const todayStr = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
    
    const weekAgo = new Date(today);
    weekAgo.setDate(today.getDate() - 7);
    const weekAgoStr = weekAgo.getFullYear() + '-' + String(weekAgo.getMonth() + 1).padStart(2, '0') + '-' + String(weekAgo.getDate()).padStart(2, '0');

    // Separate archived sessions from active sessions
    const activeSessions = allSessions.filter(s => s.session_status !== 'archived');
    const archivedSessions = allSessions.filter(s => s.session_status === 'archived');

    return {
      today: activeSessions.filter(s => s.session_date === todayStr),
      thisWeek: activeSessions.filter(s => s.session_date >= weekAgoStr && s.session_date < todayStr),
      older: activeSessions.filter(s => s.session_date < weekAgoStr),
      archived: archivedSessions
    };
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'draft': return 'default';
      case 'upcoming': return 'primary';
      case 'ongoing': return 'success';
      case 'completed': return 'warning';
      case 'archived': return 'default';
      case 'cancelled': return 'danger';
      default: return 'primary';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'draft': return '📝';
      case 'upcoming': return '🔵';
      case 'ongoing': return '🟢';
      case 'completed': return '🟠';
      case 'archived': return '⚫';
      case 'cancelled': return '❌';
      default: return '🔵';
    }
  };

  // Tasks: 1) End session (ongoing), 2) Mark all paid/no-show (completed), 3) Archive (completed + all marked)
  const getActionItems = (session) => {
    if (session.session_status === 'ongoing') {
      return []; // Column shows "Need to End Session" directly
    }
    if (session.session_status === 'completed') {
      if (canDeleteSession(session)) {
        return [{ type: 'delete', message: 'Delete the session', priority: 'high' }];
      }
      const allMarked = completedSessionAllPlayersMarked[session.id];
      if (sessionRequiresPaymentWrapUp(session) && !allMarked) {
        return [{ type: 'payment', message: 'Sort Payments', priority: 'high' }];
      }
      return [{ type: 'archive', message: 'Archive the session', priority: 'medium' }];
    }
    if (session.session_status === 'archived') {
      return [];
    }
    return [];
  };

  const renderSessionWrapUpTaskSummary = (session) => {
    if (session.session_status === 'ongoing') {
      return (
        <span className="text-sm text-warning-dark font-medium">
          ⚠️ Need to End Session
        </span>
      );
    }
    if (session.session_status === 'upcoming') {
      return (
        <span className="text-sm text-text-secondary">
          Planned — manage from Planned sessions
        </span>
      );
    }
    if (session.session_status === 'cancelled') {
      return <span className="text-sm text-text-muted">Cancelled</span>;
    }
    if (session.session_status === 'archived') {
      return null;
    }
    const actionItems = getActionItems(session);
    const pendingTasksCount = actionItems.length;
    const highPriorityCount = actionItems.filter((item) => item.priority === 'high').length;
    const isArchiveTaskOnly = pendingTasksCount === 1 && actionItems[0]?.type === 'archive';
    const isPaymentTaskOnly = pendingTasksCount === 1 && actionItems[0]?.type === 'payment';
    const isDeleteTaskOnly = pendingTasksCount === 1 && actionItems[0]?.type === 'delete';

    if (pendingTasksCount === 0) {
      return <span className="text-sm text-success-dark">✅ All done</span>;
    }
    if (isArchiveTaskOnly) {
      return (
        <span className="text-sm text-info font-medium">
          📦 Archive the session
        </span>
      );
    }
    if (isPaymentTaskOnly) {
      return (
        <span className="text-sm text-warning-dark font-medium">
          💳 Sort Payments
        </span>
      );
    }
    if (isDeleteTaskOnly) {
      return (
        <span className="text-sm text-danger-dark font-medium">
          🗑️ Delete the session
        </span>
      );
    }
    return (
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {highPriorityCount > 0 && (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-danger-light text-danger-dark">
            🔴 {highPriorityCount}
          </span>
        )}
        <span className="text-sm text-text-secondary">
          {pendingTasksCount} task{pendingTasksCount !== 1 ? 's' : ''}
        </span>
      </div>
    );
  };

  const renderSessionPastMobileCard = (session) => (
    <SessionPastMobileCard
      session={session}
      clubLogoUrl={clubLogoUrl}
      clubName={clubName}
      completedSessionAllPlayersMarked={completedSessionAllPlayersMarked}
      taskSummary={renderSessionWrapUpTaskSummary(session)}
      onEnterSession={(sessionId) => navigate(`/club/${clubId}/session/${sessionId}`)}
      onReviewAndArchive={(sessionId) => navigate(`/club/${clubId}/session-wrapup/${sessionId}`)}
      onArchive={async (sessionId) => {
        try {
          await updateSessionStatus(sessionId, 'archived');
        } catch (e) {
          console.error('Failed to archive session:', e);
        }
      }}
      onDelete={setSessionToDelete}
      canDelete={canDeleteSession(session)}
      hasPaymentRecords={!!sessionsWithPayments[session.id]}
    />
  );

  const FilterPanel = () => (
    <MobileSearchField
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      placeholder="Search sessions"
      aria-label="Search sessions"
      showIcon
      className="form-input w-full !py-2"
    />
  );


  const TimeSection = ({ title, sessions, iconType, emptyMessage }) => {
    if (sessions.length === 0) return null;
    const iconTone = MANAGEMENT_ICON_TONES[iconType];

    return (
      <div className="mb-8">
        <div className="flex items-center space-x-2 mb-4">
          <span className={`shrink-0 ${iconTone?.text ?? 'text-text-secondary'}`}>
            <ClubSectionIcon type={iconType} />
          </span>
          <Heading level={3} className="text-text-primary">{title}</Heading>
          <span className="text-sm text-text-muted">({sessions.length} sessions)</span>
        </div>
        <ul className="lg:hidden space-y-3">
            {sessions.map((session) => (
              <li key={session.id}>{renderSessionPastMobileCard(session)}</li>
            ))}
          </ul>
          <div className="hidden lg:block overflow-x-auto rounded-lg border border-border-medium bg-surface">
            <table className="w-full table-fixed">
              <thead className="bg-surface-subtle border-b border-border-medium">
                <tr>
                  <th className="w-32 px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Date & Time</th>
                  <th className="w-64 px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Session</th>
                  <th className="w-20 px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Signups</th>
                  <th className="w-20 px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Players</th>
                  <th className="w-20 px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Games</th>
                  <th className="w-24 px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Status</th>
                  <th className="w-32 px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Tasks</th>
                  <th className="w-24 px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {sessions.map((session) => (
                  <tr key={session.id} className="hover:bg-surface-subtle">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-text-primary">{formatDate(session.session_date)}</div>
                        <div className="text-sm text-text-muted">{formatTime(session.start_time)}</div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div>
                        <div className="text-sm font-medium text-text-primary">{session.name}</div>
                        <div className="text-sm text-text-muted">{session.session_types?.name || 'N/A'}</div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-text-primary text-left">
                      {session.current_players}/{session.max_players}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-text-primary text-left">
                      {session.players_count ?? 0}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-text-primary text-left">
                      {session.games_count ?? 0}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-left">
                      <div className="flex items-center space-x-2">
                        <StatusBadge variant={getStatusVariant(session.session_status)} size="small">
                          {getSessionStatusLabel(session.session_status)}
                        </StatusBadge>
                        {session.session_status === 'ongoing' && (
                          <span className="text-xs text-text-muted" title="End session before payments and archive">
                            🔒
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-left">
                      {renderSessionWrapUpTaskSummary(session)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-left overflow-visible">
                      <div
                        ref={openActionsSession?.id === session.id ? actionsDropdownRef : undefined}
                        className="relative inline-block"
                      >
                        <Button
                          onClick={() => setOpenActionsSession(openActionsSession?.id === session.id ? null : session)}
                          variant="outline"
                          size="small"
                          className="px-2 text-xs"
                        >
                          Actions ▾
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
      </div>
    );
  };

  const getProgressPercentage = (session) => {
    if (session.session_status === 'ongoing') return 25;
    if (session.session_status === 'completed') {
      const allMarked = completedSessionAllPlayersMarked[session.id];
      if (!sessionRequiresPaymentWrapUp(session)) return 75;
      return allMarked ? 75 : 50; // 50 = mark paid/no-show, 75 = ready to archive
    }
    if (session.session_status === 'archived') return 100;
    return 0;
  };


  const filterSessions = (sessions) => {
    if (!searchTerm) return sessions;
    
    const searchLower = searchTerm.toLowerCase();
    return sessions.filter(session => 
      session.name.toLowerCase().includes(searchLower) ||
      session.session_types?.name.toLowerCase().includes(searchLower) ||
      formatDate(session.session_date).toLowerCase().includes(searchLower)
    );
  };

  const excludeInboxSessions = (sessionList) => {
    if (!embedded || !excludeSessionIds?.size) return sessionList;
    return sessionList.filter((session) => !excludeSessionIds.has(session.id));
  };

  const filterHubPastSessions = (sessionList) => {
    if (!embedded) return sessionList;
    return sessionList.filter(isHubPastSessionListItem);
  };

  const getFilteredSessionsByTime = () => {
    return {
      today: filterHubPastSessions(excludeInboxSessions(filterSessions(sessionsByTime.today))),
      thisWeek: filterHubPastSessions(filterSessions(sessionsByTime.thisWeek)),
      older: filterHubPastSessions(filterSessions(sessionsByTime.older)),
      archived: filterSessions(sessionsByTime.archived),
    };
  };

  // Get paginated archived sessions
  const getPaginatedArchivedSessions = () => {
    const filteredArchived = filterSessions(sessionsByTime.archived);
    const startIndex = (archivedPagination.currentPage - 1) * archivedPagination.pageSize;
    const endIndex = startIndex + archivedPagination.pageSize;
    return filteredArchived.slice(startIndex, endIndex);
  };

  // Handle archived pagination
  const handleArchivedPageChange = (newPage) => {
    setArchivedPagination(prev => ({
      ...prev,
      currentPage: newPage
    }));
  };



  const filteredSessions = getFilteredSessionsByTime();
  const showTimeGroups = variant !== 'archived';
  const showArchived = variant !== 'past';
  const nestArchivedInCollapsible = variant === 'all';

  const archivedList = filteredSessions.archived.length > 0 ? (
            <div className="space-y-4">
              <ul className="lg:hidden space-y-3">
                {getPaginatedArchivedSessions().map((session) => (
                  <li key={session.id}>{renderSessionPastMobileCard(session)}</li>
                ))}
              </ul>
              <div className="hidden lg:block overflow-x-auto rounded-lg border border-border-medium bg-surface">
                <table className="w-full table-fixed">
                  <thead className="bg-surface-subtle border-b border-border-medium">
                    <tr>
                      <th className="w-32 px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Date & Time</th>
                      <th className="w-64 px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Session</th>
                      <th className="w-20 px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Signups</th>
                      <th className="w-20 px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Players</th>
                      <th className="w-20 px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Games</th>
                      <th className="w-24 px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Status</th>
                      <th className="w-32 px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Tasks</th>
                      <th className="w-24 px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {getPaginatedArchivedSessions().map((session) => (
                    <tr key={session.id} className="hover:bg-surface-subtle">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-text-primary">{formatDate(session.session_date)}</div>
                          <div className="text-sm text-text-muted">{formatTime(session.start_time)}</div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div>
                          <div className="text-sm font-medium text-text-primary">{session.name}</div>
                          <div className="text-sm text-text-muted">{session.session_types?.name || 'N/A'}</div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-text-primary text-left">
                        {session.current_players}/{session.max_players}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-text-primary text-left">
                        {session.players_count ?? 0}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-text-primary text-left">
                        {session.games_count ?? 0}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-left">
                        <div className="flex items-center space-x-2">
                          <StatusBadge variant={getStatusVariant(session.session_status)} size="small">
                            {getSessionStatusLabel(session.session_status)}
                          </StatusBadge>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-left">
                        {renderSessionWrapUpTaskSummary(session)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-left overflow-visible">
                        <Button
                          onClick={() => navigate(`/club/${clubId}/session-wrapup/${session.id}`)}
                          variant="outline"
                          size="small"
                          className="px-2 text-xs"
                        >
                          View session
                        </Button>
                      </td>
                    </tr>
                    ))}
                  </tbody>
                </table>
              </div>

            {/* Pagination Controls */}
            {archivedPagination.totalItems > archivedPagination.pageSize && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 bg-surface-subtle border-t border-border-medium">
                <div className="flex items-center text-sm text-text-secondary">
                  <span>
                    Showing {((archivedPagination.currentPage - 1) * archivedPagination.pageSize) + 1} to{' '}
                    {Math.min(archivedPagination.currentPage * archivedPagination.pageSize, archivedPagination.totalItems)} of{' '}
                    {archivedPagination.totalItems} archived sessions
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    onClick={() => handleArchivedPageChange(archivedPagination.currentPage - 1)}
                    disabled={archivedPagination.currentPage === 1}
                    variant="outline"
                    size="small"
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-text-secondary">
                    Page {archivedPagination.currentPage} of {Math.ceil(archivedPagination.totalItems / archivedPagination.pageSize)}
                  </span>
                  <Button
                    onClick={() => handleArchivedPageChange(archivedPagination.currentPage + 1)}
                    disabled={archivedPagination.currentPage >= Math.ceil(archivedPagination.totalItems / archivedPagination.pageSize)}
                    variant="outline"
                    size="small"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500">
            <div className="text-4xl mb-2">📁</div>
            <Text>No archived sessions found</Text>
          </div>
        );

  return (
    <div className="space-y-6">
      <FilterPanel />

      {showTimeGroups ? (
        <>
          <TimeSection
            title={embedded ? 'Other sessions today' : "Today's Sessions"}
            sessions={filteredSessions.today}
            iconType="sessions"
          />
          <TimeSection
            title="This Week's Sessions"
            sessions={filteredSessions.thisWeek}
            iconType="planning"
          />
          <TimeSection
            title="Older Sessions"
            sessions={filteredSessions.older}
            iconType="past-sessions"
          />
        </>
      ) : null}

      {showArchived ? (
        nestArchivedInCollapsible ? (
          <CollapsibleSection
            title="Archived Sessions"
            iconType="archive"
            defaultExpanded={false}
            className="mb-8"
          >
            {archivedList}
          </CollapsibleSection>
        ) : (
          archivedList
        )
      ) : null}

      {/* Actions dropdown portal (rendered in body so it isn't clipped by table overflow) */}
      {openActionsSession && dropdownPosition && createPortal(
        <div
          ref={actionsPortalRef}
          className="rounded-lg border-2 border-slate-300 bg-slate-50 py-1 z-[2000] shadow-[0_4px_20px_rgba(0,0,0,0.12),0_0_0_1px_rgba(0,0,0,0.05)]"
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: DROPDOWN_WIDTH,
            minWidth: DROPDOWN_WIDTH,
            zIndex: 2100,
          }}
        >
          {openActionsSession.session_status === 'ongoing' ? (
            <button
              type="button"
              onClick={() => { navigate(`/club/${clubId}/session/${openActionsSession.id}`); setOpenActionsSession(null); }}
              className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 inline-flex items-center gap-2"
            >
              <svg className="w-4 h-4 shrink-0 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
              </svg>
              Enter
            </button>
          ) : openActionsSession.session_status === 'completed' ? (
            <button
              type="button"
              onClick={() => { navigate(`/club/${clubId}/session-wrapup/${openActionsSession.id}`); setOpenActionsSession(null); }}
              className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 inline-flex items-center gap-2"
            >
              <svg className="w-4 h-4 shrink-0 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Review & archive
            </button>
          ) : openActionsSession.session_status === 'archived' ? (
            <button
              type="button"
              onClick={() => { navigate(`/club/${clubId}/session-wrapup/${openActionsSession.id}`); setOpenActionsSession(null); }}
              className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 inline-flex items-center gap-2"
            >
              <svg className="w-4 h-4 shrink-0 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              View session
            </button>
          ) : null}
          {openActionsSession.session_status === 'completed' && completedSessionAllPlayersMarked[openActionsSession.id] && (
            <>
              <div className="border-t border-slate-100 my-1" />
              <button
                type="button"
                onClick={async () => {
                  try {
                    await updateSessionStatus(openActionsSession.id, 'archived');
                    setOpenActionsSession(null);
                  } catch (e) {
                    console.error('Failed to archive session:', e);
                  }
                }}
                className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 font-medium inline-flex items-center gap-2"
              >
                <svg className="w-4 h-4 shrink-0 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
                Archive session
              </button>
            </>
          )}
          {openActionsSession.session_status === 'completed' && isCompetitiveSession(openActionsSession) ? (
            <>
              <div className="border-t border-slate-100 my-1" />
              {canDeleteSession(openActionsSession) ? (
                sessionsWithPayments[openActionsSession.id] ? (
                  <div className="px-3 py-2 inline-flex items-center gap-2">
                    <svg className="w-4 h-4 shrink-0 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <div>
                      <span className="block text-sm text-slate-400">Delete</span>
                      <p className="text-xs text-slate-500 mt-0.5">Session has payment records. Undo payments first.</p>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => { setSessionToDelete(openActionsSession); setOpenActionsSession(null); }}
                    className="w-full text-left px-3 py-2 text-sm text-danger hover:bg-danger-light inline-flex items-center gap-2"
                  >
                    <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                  </button>
                )
              ) : (
                <div className="px-3 py-2 inline-flex items-center gap-2">
                  <svg className="w-4 h-4 shrink-0 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <div>
                    <span className="block text-sm text-slate-400">Delete</span>
                    <p className="text-xs text-slate-500 mt-0.5">Only available when no games have been played.</p>
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>,
        document.body
      )}

      {/* Delete session confirmation (0-game completed competitive only) */}
      <ConfirmationModal
        isOpen={!!sessionToDelete}
        onClose={() => setSessionToDelete(null)}
        onConfirm={handleConfirmDeleteSession}
        title="Delete Session"
        message={sessionToDelete
          ? `Delete "${sessionToDelete.name || 'this session'}"? This session has no games played. The session and all signups will be removed. This cannot be undone.`
          : ''}
        confirmText="Delete Session"
        cancelText="Keep Session"
        isDangerous={true}
      />

      {/* Refresh Button */}
      <div className="flex justify-center">
            <Button
              onClick={() => refreshSessions({ syncHub: true })}
              variant="outline"
              size="small"
              disabled={sessionsLoading}
            >
          {sessionsLoading ? 'Refreshing...' : 'Refresh Sessions'}
            </Button>
          </div>

      {/* No Sessions Message */}
      {filteredSessions.today.length === 0 && filteredSessions.thisWeek.length === 0 && filteredSessions.older.length === 0 && filteredSessions.archived.length === 0 && !sessionsLoading && (
        <Card variant="default" size="medium" className="text-center p-8">
          <div className="text-4xl mb-4">📅</div>
          <Heading level={3} className="mb-2">No Sessions</Heading>
          <Text className="text-slate-600">
            {Object.values(sessionsByTime).flat().length === 0 
              ? "There are no sessions to monitor." 
              : "No sessions match your current filters. Try adjusting your search criteria."}
          </Text>
        </Card>
      )}


      {/* Session Details Modal */}
      {selectedSession && sessionActivityStatus && (
      <Modal
        isOpen
        onClose={() => {
          setSelectedSession(null);
          setSessionActivityStatus(null);
        }}
      >
        <ModalContent size="medium" padding={false} className="flex min-h-0 flex-col overflow-hidden">
          <div className="min-h-0 flex-1 overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-6">
                <Heading level={3}>Session Details</Heading>
                <button
                  onClick={() => {
                    setSelectedSession(null);
                    setSessionActivityStatus(null);
                  }}
                  className="text-text-muted hover:text-text-primary transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-semibold text-slate-900">Session:</span> {sessionActivityStatus.session_name}
                  </div>
                  <div>
                    <span className="font-semibold text-slate-900">Status:</span> 
                    <StatusBadge variant={getStatusVariant(sessionActivityStatus.session_status)} size="small" className="ml-1">
                      {sessionActivityStatus.session_status}
                    </StatusBadge>
                  </div>
                  <div>
                    <span className="font-semibold text-slate-900">Games Played:</span> {sessionActivityStatus.games_count}
                  </div>
                  <div>
                    <span className="font-semibold text-slate-900">Hours Since Activity:</span> {sessionActivityStatus.hours_since_last_activity?.toFixed(1)}
                  </div>
                </div>
              </div>

              <div className="mt-8 flex justify-end">
                <Button
                  onClick={() => {
                    setSelectedSession(null);
                    setSessionActivityStatus(null);
                  }}
                  variant="outline"
                >
                  Close
                </Button>
              </div>
          </div>
        </ModalContent>
      </Modal>
      )}

    </div>
  );
};

export default SessionMonitoring;